import { Route } from 'vue-router';
/**
 * This mixin allows the configuration of a "back route" via route params:
 *
 * When a programmatic route transition happens, the origin route can define
 * a "back route", which the target route MAY return to if the component decides so.
 *
 * This is mainly useful for implementing a "close" or "back" button in a view that
 * returns to the configured back route, instead of just "history back" or a default route.
 *
 * This mixin should be included in the TARGET component, and defines the following parts:
 *
 * - a "backRoute" data property, containing the back route config from the source route's params
 * - a mounted() hook that reads the backRoute params from a source route
 *
 * The main idea is that we can go back to a specific route when using the "close" btn,
 * instead of just route to the predefined route. This is useful if the user is transitioned to this
 * record, but would like to "go back" to the original route on close.
 *
 * Example:
 *
 * Change to a target route programmatically, give the return route as parameter.
 * In this example, the back route is the calling route itself:
 *
 * <code>
 * this.$router.push({name: 'my-target-route', params: {backRoute: { route: this.$route, displayText: 'Back to me'}}})
 * </code>
 *
 * In the target component, you can define a "close" method to go back to where you came:
 *
 * <code>
 * // target component:
 * import BackRouteMixin from '@/mixins/backroute';
 * export default {
    mixins: [BackRouteMixin],
 *  methods: {
 *   onClose() {
 *     let backRoute = this.getBackRouteConfig()?.route || {name: 'default-back-route'};
 *     this.$router.push(backRoute);
 *   }
 *  }
 * };
 * </code>
 */

/**
 * BackRouteConfig is an object, set by a calling route as route parameters,
 * that delivers the following information:
 * {
 *      route: The route config for a "back route", when using the "close" btn
 *      displayText: The display text for the "close" btn to display
 * }
 */
export type BackRouteConfig = {
    route: Route | string;
    displayText?: string;
};

const backRouteMixin = {
    data() {
        return {
            backRoute: null,
        } as {
            backRoute: BackRouteConfig | null;
        };
    },

    mounted() {
        // we save a backroute for the close btn, if one is delivered:
        // The source route must set a backRoute in the Route param:
        if (this.$route?.params?.backRoute) {
            this.backRoute = this.$route.params.backRoute;
        }
    },

    methods: {
        getBackRouteConfig(this: any): BackRouteConfig | null {
            return this.backRoute;
        },
    },
} as any;

export default backRouteMixin;
